import React from "react";
import "./legal.scss";

function TestersAgreement({ onAgree, noOptIn = false }) {
  return (
    <div className="legalPage">
      <h1>Beta Testing Agreement</h1>

      <p>
        Thank you for joining the Speqqle Beta Testing Program. This agreement
        outlines what the program entails and how we will collaborate during
        the beta period. We are excited to hear your thoughts, needs, and
        experiences — your feedback will help shape the future of Speqqle.
      </p>

      <div className="section">
        <h2>1. Purpose of Beta Testing</h2>
        <p>
          The Speqqle Beta Testing Program is designed to gather insights,
          identify potential improvements, and evaluate new features through
          real-world usage. Your participation helps us understand how the
          platform performs and where enhancements are needed.
        </p>
      </div>

      <div className="section">
        <h2>2. Role of the Beta Tester</h2>
        <p>
        As a beta tester, you are welcome to:
        </p>
        <ul>
          <li>Use the platform as you would in real-life scenarios.</li>
          <li>
            Share your experience with us, including feedback on usability,
            performance, or desired improvements.
          </li>
          <li>
            Let us know if you encounter bugs or missing functionality — even a
            short message is appreciated.
          </li>
        </ul>
        <p>
          There is no obligation to submit regular reports. However, if we
          reach out to you with specific questions or requests, we ask that you
          respond in a timely and constructive manner.
        </p>
      </div>

      <div className="section">
        <h2>3. Our Commitments to You</h2>
        <p>
          In return for your participation, we will:
        </p>
        <ul>
          <li>Give you access to new beta features before public release.</li>
          <li>Keep you informed about updates, changes, and improvements.</li>
          <li>
            Consider your feedback carefully and use it to prioritize our
            development roadmap.
          </li>
          <li>
            Optionally acknowledge your contribution (with your consent) in our
            release materials or documentation.
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>4. Confidentiality & Use of Feedback</h2>
        <details>
          <summary style={{ cursor: "pointer" }}>Click to expand</summary>
          <p>
            Please treat the platform and any unreleased features or
            documentation as confidential. You may not share access,
            screenshots, or any internal details about the platform without our
            written permission.
          </p>
          <p>
            All feedback you provide may be used by Speqqle to improve the
            platform. By submitting feedback, you grant Speqqle SA the right to
            use it without any obligation for compensation.
          </p>
        </details>
      </div>

      <div className="section">
        <h2>5. Privacy and Data Protection</h2>
        <details>
          <summary style={{ cursor: "pointer" }}>Click to expand</summary>
          <p>
            We prioritize your privacy and are committed to protecting your
            personal data. All data collected during the beta testing phase will
            be handled in strict compliance with GDPR and Swiss data protection
            laws. For more details, please review our{" "}
            <a
              href={`${window.location.origin}/privacy-policy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href={`${window.location.origin}/terms-of-use`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </a>
            .
          </p>
          <p>
            During beta testing, certain personal data may be collected,
            including your name, email address, and any other information
            necessary to facilitate feedback collection and communication. This
            data will not be anonymized during the testing phase to ensure that
            we can reach out to you for clarifications or additional input if
            needed.
          </p>
          <p>
            To participate as an beta tester, you will be required to provide
            explicit consent and opt-in to allow us to collect, store, and
            process this data. After the beta testing phase is complete, we will
            either anonymize or delete your personal data upon your request, in
            accordance with applicable data protection regulations.
          </p>
        </details>
      </div>

      <div className="section">
        <h2>6. Limitation of Liability</h2>
        <details>
          <summary style={{ cursor: "pointer" }}>Click to expand</summary>
          <p>
            The software provided during the beta testing phase is a pre-release
            version and is made available &quot;as is.&quot; We do not guarantee
            its performance, stability, or uptime. Our liability is limited to
            the fullest extent permitted by law.
          </p>
        </details>
      </div>

      <div className="section">
        <h2>7. Termination</h2>
        <details>
          <summary style={{ cursor: "pointer" }}>Click to expand</summary>
          <p>
            We reserve the right to terminate your participation in the beta
            testing program at any time. You may also withdraw from the program
            at any time by notifying us.
          </p>
          <p>
            After termination, please delete any output, reports, or downloads
            that originated from the platform.
          </p>
        </details>
      </div>

      <div className="section">
        <h2>8. Links to Other Documents</h2>
        <p>For more information, please refer to the following documents:</p>
        <ul>
          <li>
            <a
              href={`${window.location.origin}/privacy-policy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </li>
          <li>
            <a
              href={`${window.location.origin}/terms-of-use`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </a>
          </li>
          <li>
            <a
              href={`${window.location.origin}/manual`}
              //   href="https://app.speqqle.com/manualalpha/"
            >
              User Manual for our beta testers
            </a>
          </li>
          <li>
            <a href={`${window.location.origin}/feedback`}>Feedback Form</a>
          </li>
        </ul>
      </div>

      <div className="section">
        <h2>9. Acknowledgment and Consent</h2>
        <p>
          By participating in the beta testing program, you acknowledge that you
          have read and understood this agreement, and you agree to its terms
          and conditions. You also acknowledge that the software is a
          pre-release version and may contain defects that could cause data
          loss, instability, or other issues.
        </p>
        <p>
          Additionally, by opting in, you provide explicit consent for us to
          process and store your non-anonymized data for the purposes of
          feedback collection, issue resolution, and product improvement, as
          outlined in our{" "}
          <a
            href={`${window.location.origin}/privacy-policy`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>
        <p>
          To confirm your participation and provide your consent, please click
          the &quot;Agree &amp; Opt-In&quot; button below:
        </p>
        {!noOptIn ? (
          <button className="btn-primary" onClick={() => onAgree()}>
            Agree &amp; Opt-In
          </button>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}

export default TestersAgreement;
